/**
 * Command Palette (M10.5 · ⌘K).
 *
 * First-class natural-language navigation. Opens with ⌘K / Ctrl-K from any
 * page. Fuzzy-matches against a curated action list scoped to what the
 * platform can actually do — no invented commands. Actions route the user
 * or invoke a callback (demo reset, workspace switch, etc.).
 *
 * This is a router, not an LLM. Every command in the list must be a real
 * capability the platform supports today.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Command as CmdIcon, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useCurrentWorkspace } from "@/entities/workspace";
import { useSurfacePolicy } from "@/entities/auth/useSurfacePolicy";
import { useWorkspaceStore } from "@/app/store/workspace-store";
import { http } from "@/shared/api/http";

interface Action {
  id: string;
  label: string;
  hint?: string;
  group: string;
  keywords?: string;
  run: () => void | Promise<void>;
}

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const nav = useNavigate();
  const ws = useCurrentWorkspace();
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const setActive = useWorkspaceStore((s) => s.selectWorkspace);
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const { canViewInternalDiagnostics } = useSurfacePolicy();

  const resetDemo = useMutation({
    mutationFn: () => http("/demo/reset", { method: "POST" }),
    onSuccess: () => { toast.success("Demo workspace rebuilt"); qc.invalidateQueries(); onClose(); },
  });

  const actions = useMemo<Action[]>(() => {
    const nav_ = (path: string, label: string, group: string, keywords?: string): Action => ({
      id: `nav:${path}`, label, group, keywords, hint: path,
      run: () => { nav(path); onClose(); },
    });
    const list: Action[] = [
      nav_("/", "Open Command Center", "Navigate", "home dashboard executive briefing"),
      nav_("/mission-control", "Open Mission Control", "Navigate", "calendar publishing schedule queue"),
      nav_("/studio", "Open Studio", "Navigate", "ai studio creative brief generate"),
      nav_("/library", "Open Creative Library", "Navigate", "assets masonry gallery"),
      nav_("/media", "Open Media Engine", "Navigate", "reel video image builder"),
      nav_("/intelligence", "Open Intelligence", "Navigate", "advisor opportunities executive report"),
      nav_("/brand-brain", "Open Brand Brain", "Navigate", "voice audience products"),
      nav_("/publishing", "Open Publishing Vault", "Navigate", "oauth channels connections"),
      nav_("/launch-readiness", "Open Launch Readiness", "Navigate", "score pillars go-live"),
      nav_("/billing", "Open Billing", "Navigate", "stripe subscription plan"),
      nav_("/history", "Open Generation History", "Navigate", "ai logs history"),
      nav_("/ops", "Open Content Operations", "Navigate", "approval review"),
      nav_("/health", "Open Health & Version", "Navigate", "status uptime"),
      // Generation shortcuts
      { id: "act:generate", group: "Generate", label: "Generate a new campaign", keywords: "campaign brief studio", run: () => { nav("/studio"); onClose(); } },
      { id: "act:reel", group: "Generate", label: "Build a reel", keywords: "reel media video builder", run: () => { nav("/media"); onClose(); } },
      { id: "act:tomorrow", group: "Schedule", label: "Schedule tomorrow's posts", keywords: "queue schedule tomorrow", run: () => { nav("/mission-control"); onClose(); } },
      { id: "act:publishnow", group: "Publish", label: "Publish now — review approvals", keywords: "approve publish now content ops", run: () => { nav("/ops"); onClose(); } },
      { id: "act:cost", group: "Insights", label: "Show AI cost trend", keywords: "spend ai cost billing dashboard", run: () => { nav("/dashboard"); onClose(); } },
      { id: "act:launchdemo", group: "Demo", label: "Launch demo workspace", keywords: "demo aurora competition mode reset", run: () => resetDemo.mutate() },
      { id: "act:reset-demo", group: "Demo", label: "Reset demo workspace", keywords: "reset seed aurora", run: () => resetDemo.mutate() },
    ];
    if (!canViewInternalDiagnostics) {
      for (const path of ["/health", "/launch-readiness"]) {
        const index = list.findIndex((action) => action.id === `nav:${path}`);
        if (index >= 0) list.splice(index, 1);
      }
    }
    for (const w of workspaces) {
      list.push({
        id: `ws:${w.id}`,
        group: "Switch workspace",
        label: `Switch to ${w.profile?.display_name || w.slug}`,
        keywords: `${w.slug} ${w.profile?.display_name ?? ""}`,
        run: () => { setActive(w.id); toast.success(`Now in ${w.profile?.display_name || w.slug}`); onClose(); },
      });
    }
    return list;
  }, [nav, workspaces, setActive, resetDemo, onClose, canViewInternalDiagnostics]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions.filter((a) =>
      (a.label + " " + (a.keywords ?? "") + " " + a.group).toLowerCase().includes(q),
    );
  }, [actions, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  useEffect(() => setCursor(0), [query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, filtered.length - 1)); }
      if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
      if (e.key === "Enter") { e.preventDefault(); filtered[cursor]?.run(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, filtered, cursor, onClose]);

  if (!open) return null;

  const grouped = filtered.reduce<Record<string, Action[]>>((acc, a) => {
    (acc[a.group] ||= []).push(a); return acc;
  }, {});

  return (
    <AnimatePresence>
      <motion.div
        key="cmdk"
        data-testid="command-palette"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.16 }}
        className="fixed inset-0 z-50 grid place-items-start justify-items-center bg-[var(--md-ink-0)]/70 backdrop-blur-md px-4 pt-[12vh]"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 8, opacity: 0 }}
          transition={{ duration: 0.22, ease: [0.2, 0.7, 0.3, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="md-panel--glass w-full max-w-2xl overflow-hidden rounded-2xl shadow-[var(--shadow-lg)]"
        >
          <div className="flex items-center gap-3 border-b border-[color:var(--md-line)] px-5 py-4">
            <Search className="h-4 w-4 text-[var(--md-fog)]" />
            <input
              ref={inputRef}
              data-testid="command-palette-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search commands · in ${ws?.profile?.display_name ?? "Orkexra"} …`}
              className="flex-1 bg-transparent text-[15px] text-[var(--md-ivory-0)] placeholder:text-[var(--md-smoke)] focus:outline-none"
            />
            <span className="rounded-md border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-[var(--md-fog)]">Esc</span>
          </div>
          <div className="max-h-[52vh] overflow-y-auto">
            {Object.entries(grouped).length === 0 ? (
              <div className="p-10 text-center text-sm text-[var(--md-fog)]">No commands match that query.</div>
            ) : (
              Object.entries(grouped).map(([group, list]) => (
                <div key={group} className="px-3 py-2">
                  <div className="px-3 py-1 md-eyebrow">{group}</div>
                  {list.map((a) => {
                    const idx = filtered.indexOf(a);
                    const on = idx === cursor;
                    return (
                      <button
                        key={a.id}
                        data-testid={`cmdk-item-${a.id}`}
                        onMouseEnter={() => setCursor(idx)}
                        onClick={() => a.run()}
                        className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm ${
                          on ? "bg-[var(--md-ink-2)] text-[var(--md-ivory-0)]" : "text-[var(--md-fog)]"
                        }`}
                      >
                        <span>{a.label}</span>
                        {a.hint ? (
                          <span className="hidden font-mono text-[10px] uppercase tracking-widest text-[var(--md-smoke)] sm:inline">
                            {a.hint}
                          </span>
                        ) : null}
                        {on ? <ArrowRight className="h-3.5 w-3.5 text-[var(--md-amber)]" /> : null}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
          <div className="flex items-center justify-between border-t border-[color:var(--md-line)] px-5 py-3 md-eyebrow">
            <span className="flex items-center gap-2">
              <CmdIcon className="h-3.5 w-3.5" /> Command · K
            </span>
            <span>{filtered.length} results</span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Global keyboard trigger + provider ─────────────────────────
export function CommandPaletteProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <>
      {children}
      <CommandPalette open={open} onClose={() => setOpen(false)} />
    </>
  );
}
